/**
 * Irmão: `src/app/components/filters/FilterChip.tsx`.
 *
 * Duplicação deliberada (openspec/changes/flashcards-revisao-redesign/design.md,
 * Decisão 2). Não reusar `components/filters/`: store, persistência e efeito de
 * aplicação são de outra tela. Uma correção visual aqui deve ser conferida
 * no irmão, e o inverso.
 */
import { forwardRef, KeyboardEvent, ReactNode } from "react";
import { Check } from "lucide-react";
import { REVISAO_FILTER_FOCUS_RING, revisaoFilterCx } from "./tokens";

/**
 * Semantic weight of a chip. `select` is the one selection treatment used by
 * every filter option in the product. `danger` exists only for options whose
 * own meaning is failure — "Incorretas" — never as emphasis (§23.6).
 */
export type RevisaoChipTone = "select" | "danger" | "warn";

export interface RevisaoFilterChipProps {
  label: string;
  selected?: boolean;
  /**
   * Rendered as `aria-disabled`, never the native `disabled` attribute: a
   * natively disabled button drops out of the tab order and out of
   * `RevisaoFilterChipGroup`'s roving-tabindex set
   * (`button[data-chip]:not([disabled])`), which would make the chip
   * unreachable by keyboard — the opposite of "indisponível, não invisível".
   * `onToggle` is still skipped while disabled.
   *
   * A future test against this: Playwright's own `locator.click()` treats
   * `aria-disabled="true"` as not-actionable and times out waiting for it to
   * become "enabled" — that's Playwright's auto-wait, not a broken chip. Use
   * `.click({ force: true })` or a raw DOM click to exercise the guard.
   */
  disabled?: boolean;
  /** id(s) of the element explaining why the chip is disabled — wired as `aria-describedby`. Meaningful only alongside `disabled`. */
  describedBy?: string;
  icon?: ReactNode;
  tone?: RevisaoChipTone;
  /** Radio semantics instead of toggle semantics. */
  role?: "radio";
  onToggle?: () => void;
  tabIndex?: number;
  onKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void;
  className?: string;
}

const TONE_SELECTED: Record<RevisaoChipTone, string> = {
  select: "border-select/[0.42] bg-select/[0.14] text-select-ink",
  danger: "border-danger/[0.42] bg-danger/[0.14] text-danger-ink",
  warn: "border-warn/[0.42] bg-warn/[0.14] text-warn-ink",
};

/**
 * The chip treatment, exported so single-choice groups can render the exact
 * same control without duplicating the styling (§24.2).
 *
 * Unselected chips stay neutral in both themes — a grid where every chip
 * carries a tinted border stops communicating what "selected" means (§8.1).
 */
export function revisaoChipClassName({
  selected = false,
  tone = "select",
  disabled = false,
}: {
  selected?: boolean;
  tone?: RevisaoChipTone;
  disabled?: boolean;
}): string {
  return revisaoFilterCx(
    "inline-flex h-9 max-w-full items-center gap-2 rounded-chip border px-3.5 sm:max-w-[18rem]",
    "text-[13px] font-medium leading-none transition-colors duration-150",
    REVISAO_FILTER_FOCUS_RING,
    disabled
      ? // Indisponibilidade não é erro: ink-muted sobre surface-muted, nunca
        // danger, e nunca o tom `select` — disabled sobrepõe selected.
        "cursor-not-allowed border-edge bg-surface-muted text-ink-muted"
      : selected
        ? TONE_SELECTED[tone]
        : "border-edge bg-canvas text-ink-secondary hover:border-edge-strong hover:bg-surface-hover hover:text-ink",
  );
}

/**
 * A single filter option.
 *
 * Selection is carried by the check mark and aria-pressed as well as by colour,
 * so it survives a screen reader and a colour-blind reader alike (§23.16).
 * Labels truncate at the end, never at the start, and always keep the full text
 * available through `title` and `aria-label` (§23.13).
 */
const RevisaoFilterChip = forwardRef<HTMLButtonElement, RevisaoFilterChipProps>(
  function RevisaoFilterChip(
    {
      label,
      selected = false,
      disabled = false,
      describedBy,
      icon,
      tone = "select",
      role,
      onToggle,
      tabIndex,
      onKeyDown,
      className,
    },
    ref,
  ) {
    const selectionProps =
      role === "radio"
        ? ({ role: "radio", "aria-checked": selected } as const)
        : ({ "aria-pressed": selected } as const);

    return (
      <button
        ref={ref}
        type="button"
        data-chip
        aria-disabled={disabled || undefined}
        aria-describedby={describedBy}
        title={label}
        aria-label={label}
        tabIndex={tabIndex}
        onKeyDown={onKeyDown}
        onClick={disabled ? undefined : onToggle}
        className={revisaoFilterCx(revisaoChipClassName({ selected, tone, disabled }), className)}
        {...selectionProps}
      >
        {icon ? (
          <span className="shrink-0 [&>svg]:h-4 [&>svg]:w-4" aria-hidden="true">
            {icon}
          </span>
        ) : null}
        <span className="truncate">{label}</span>
        {selected ? (
          <Check className="ml-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={3} aria-hidden="true" />
        ) : null}
      </button>
    );
  },
);

export default RevisaoFilterChip;
